/**
 * Preferences → AI tools: the local AI tools Denker can run (sign-in and
 * update state per tool) and this month's AI usage.
 */
import {
  Button,
  Icons,
  CONCEPT_ICONS,
  PocketRow,
  PocketSection,
} from "@/components/ui";
import { formatNumber, useT } from "@/i18n";
import { settingsAiToolsMessages } from "@/i18n/messages/settings-ai-tools";
import { isTauri } from "@/lib/is-tauri";
import { getDesktopDownloadUrl } from "@/lib/desktop-download";
import { openExternalUrl } from "@/lib/open-external-url";
import { useCompanionStore } from "@/stores/companion-store";
import { personalRuntime } from "@/desktop/onboarding/post-demo-runtime-preference";
import {
  CONNECTION_TOOLS,
  runtimeStatus,
  useConnectionRuntimeRefresh,
} from "./connections-state";
import { useSettingsNav } from "./settings-navigation";
import { useMonthlyUsageSummary } from "./usage-state";

export function DesktopToolsExplanation() {
  const t = useT(settingsAiToolsMessages);
  return (
    <PocketRow
      testId="connections-desktop"
      leading={<CONCEPT_ICONS.agent className="h-4 w-4 text-muted" />}
      title={t("runsInDesktopApp")}
      trailing={
        <Button
          size="sm"
          variant="secondary"
          data-testid="connections-download"
          onClick={() => void openExternalUrl(getDesktopDownloadUrl())}
        >
          {t("download")}
        </Button>
      }
    />
  );
}

/** Long German totals would push the row title off, so the count truncates. */
function TokenCount({ count }: { count: number }) {
  const t = useT(settingsAiToolsMessages);
  const text = t("tokens", { count, formatted: formatNumber(count) });
  return (
    <span className="block max-w-40 truncate" title={text}>
      {text}
    </span>
  );
}

export function AiToolsPage() {
  const t = useT(settingsAiToolsMessages);
  const nav = useSettingsNav();
  const usage = useMonthlyUsageSummary();
  const runtimes = useCompanionStore((state) => state.availableRuntimes);
  const connected = useCompanionStore((state) => state.status === "connected");
  const runtimeRefresh = useConnectionRuntimeRefresh(false);
  return (
    <div data-testid="settings-page-preferences-ai-tools">
      {runtimeRefresh.error && (
        <p role="alert" className="px-2 text-appkit-small text-danger">
          {runtimeRefresh.error}
        </p>
      )}
      <PocketSection>
        {!isTauri() && !connected ? (
          <DesktopToolsExplanation />
        ) : (
          CONNECTION_TOOLS.map((tool) => {
            const runtime =
              personalRuntime(runtimes, tool.id) ??
              runtimes.find((candidate) => candidate.type === tool.id);
            const open = () =>
              nav.push({
                page: "connections-tool",
                args: { tool: tool.id, title: tool.name },
              });
            return (
              <PocketRow
                key={tool.id}
                testId={`connections-tool-${tool.id}`}
                leading={<CONCEPT_ICONS.agent className="h-4 w-4 text-muted" />}
                title={tool.name}
                secondLine={runtimeStatus(runtime)}
                status={
                  runtime?.logged_in === false
                    ? { tone: "question", text: t("notSignedIn") }
                    : undefined
                }
                chevron
                onSelect={open}
                trailing={
                  runtime?.logged_in === false || runtime?.update_available ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      data-testid={`connections-tool-action-${tool.id}`}
                      onClick={
                        runtime.logged_in !== false && runtime.managed
                          ? () => nav.open({ page: "preferences-updates" })
                          : open
                      }
                    >
                      {runtime.logged_in === false ? t("signIn") : t("update")}
                    </Button>
                  ) : undefined
                }
              />
            );
          })
        )}
      </PocketSection>
      <PocketSection>
        <PocketRow
          testId="connections-usage"
          title={t("aiUsageThisMonth")}
          secondLine={t("runsStartedThisMonth")}
          trailing={
            usage ? <TokenCount count={usage.total_tokens} /> : undefined
          }
          leading={<Icons.BarChart2 className="h-4 w-4 text-muted" />}
          chevron
          onSelect={() => nav.push({ page: "connections-usage" })}
        />
      </PocketSection>
    </div>
  );
}
